/**
 * Public `GET /api/auth/config` — flags + URLs plateforme (SSOT serveur).
 */

import { API_ENDPOINTS } from "@/constants";

export type AuthConfigExternalUrls = {
  doc: string;
  connectAccount: string;
};

export type AuthConfigResponse = {
  betaAccessGateEnabled: boolean;
  platformBaseDomain: string;
  externalUrls: AuthConfigExternalUrls;
  /** Présent uniquement hors environnement plateforme de production. */
  platformLabel?: string;
};

const ASSIST_DOCUMENT_TITLE = "SidonIAssist";

const FALLBACK_AUTH_CONFIG: AuthConfigResponse = {
  betaAccessGateEnabled: false,
  platformBaseDomain: "cncc.fr",
  externalUrls: {
    doc: "https://doc.cncc.fr/",
    connectAccount: "https://connect.cncc.fr/users/self",
  },
};

let cached: AuthConfigResponse | null = null;
let inflight: Promise<AuthConfigResponse> | null = null;

function normalizeAuthConfig(
  data: Partial<AuthConfigResponse>
): AuthConfigResponse {
  const platformLabel =
    typeof data.platformLabel === "string" && data.platformLabel.trim()
      ? data.platformLabel.trim()
      : undefined;

  return {
    betaAccessGateEnabled: Boolean(data.betaAccessGateEnabled),
    platformBaseDomain:
      typeof data.platformBaseDomain === "string" &&
      data.platformBaseDomain.trim()
        ? data.platformBaseDomain.trim()
        : FALLBACK_AUTH_CONFIG.platformBaseDomain,
    externalUrls: {
      doc:
        typeof data.externalUrls?.doc === "string" && data.externalUrls.doc
          ? data.externalUrls.doc
          : FALLBACK_AUTH_CONFIG.externalUrls.doc,
      connectAccount:
        typeof data.externalUrls?.connectAccount === "string" &&
        data.externalUrls.connectAccount
          ? data.externalUrls.connectAccount
          : FALLBACK_AUTH_CONFIG.externalUrls.connectAccount,
    },
    ...(platformLabel ? { platformLabel } : {}),
  };
}

/** Pose le titre d'onglet seulement hors prod (clé `platformLabel` présente). */
export function applyAssistDocumentTitleFromAuthConfig(
  config: AuthConfigResponse
): void {
  if (typeof document === "undefined" || !config.platformLabel) {
    return;
  }
  document.title = `[${config.platformLabel}] ${ASSIST_DOCUMENT_TITLE}`;
}

/** Snapshot sync (fallback prod tant que `ensureAuthConfig` n’a pas résolu). */
export function getCachedAuthConfig(): AuthConfigResponse {
  return cached ?? FALLBACK_AUTH_CONFIG;
}

/** Charge une fois et met en cache. En échec réseau : fallback sans cache (retry possible). */
export async function ensureAuthConfig(): Promise<AuthConfigResponse> {
  if (cached) {
    applyAssistDocumentTitleFromAuthConfig(cached);
    return cached;
  }
  if (!inflight) {
    inflight = (async () => {
      try {
        const response = await fetch(API_ENDPOINTS.AUTH_CONFIG, {
          credentials: "include",
        });
        if (!response.ok) {
          return FALLBACK_AUTH_CONFIG;
        }
        const data = (await response.json()) as Partial<AuthConfigResponse>;
        cached = normalizeAuthConfig(data);
        applyAssistDocumentTitleFromAuthConfig(cached);
        return cached;
      } catch {
        return FALLBACK_AUTH_CONFIG;
      } finally {
        inflight = null;
      }
    })();
  }
  return inflight;
}

/** @internal tests — reset cache between vitest cases */
export function resetAuthConfigCacheForTests(): void {
  cached = null;
  inflight = null;
}
